import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Container, Grid, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/shared/toast/useToast";
import { useOrgId } from "@/shared/auth/orgContext";
import { stationUsersQuery } from "@/features/users/api/usersQueries";
import { currentProfileQuery } from "@/features/users/api/profileQueries";
import { listAllComplianceRuns } from "@/features/stationAdmin/api/iaComplianceApi";
import { listStationReportsSince } from "@/features/stationAdmin/api/stationActivityApi";
import { iaKeys } from "@/features/stationAdmin/hooks/iaComplianceKeys";
import { useIaPolicyDocs } from "@/features/stationAdmin/hooks/useIaPolicyDocs";
import { useComplianceRuns } from "@/features/stationAdmin/hooks/useComplianceRuns";
import type { ReportGenUser } from "@/shared/interfaces/User";
import { PolicyDocsCard } from "./components/ia/PolicyDocsCard";
import { RunComplianceCard } from "./components/ia/RunComplianceCard";
import { ComplianceRunsCard } from "./components/ia/ComplianceRunsCard";
import { ComplianceResultsList } from "./components/ia/ComplianceResultsList";

/** Report-picker window. Recomputed per mount only — the value is baked into
 *  the query key, so a stable snapshot avoids key churn during the visit. */
const PICKER_WINDOW_MS = 30 * 24 * 3600_000;

function formatOfficer(u: ReportGenUser): string {
  const name = `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim();
  // id is null for invitation-only rows, which never make it into nameById.
  return name || u.email || u.id || "";
}

/**
 * Internal Affairs tab of the Station Admin area: manage policy PDFs and run
 * AI compliance checks of recent reports against them. The layout owns the
 * TopAppBar header (no usePageHeader here — a tab calling it would steal
 * header ownership and clear it on unmount).
 */
const StationAdminInternalAffairs: React.FC = () => {
  const orgId = useOrgId();
  const toast = useToast();
  const policyDocs = useIaPolicyDocs();

  // Run-start feedback: the results card renders below the fold on small
  // screens, so confirm the start with a toast and scroll it into view once
  // the card has committed (the effect below runs post-render, when the ref
  // is attached).
  const resultsRef = useRef<HTMLDivElement>(null);
  const [scrollPending, setScrollPending] = useState(false);
  const runsState = useComplianceRuns({
    onRunStarted: () => {
      toast.success("Compliance check started");
      setScrollPending(true);
    },
  });
  useEffect(() => {
    if (!scrollPending || !runsState.selectedRunId) return;
    resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setScrollPending(false);
  }, [scrollPending, runsState.selectedRunId]);

  // Report picker: last 30 days, on its OWN query key — never the dashboard
  // window keys, whose since-last-login start would make the picker's window
  // depend on login history.
  const [pickerWindow] = useState(() => {
    const end = Date.now();
    return {
      start: new Date(end - PICKER_WINDOW_MS).toISOString(),
      end: new Date(end).toISOString(),
    };
  });
  const reportsQuery = useQuery({
    queryKey: iaKeys.reports(
      orgId ?? "",
      pickerWindow.start,
      pickerWindow.end,
    ),
    queryFn: ({ signal }) =>
      listStationReportsSince(pickerWindow.start, pickerWindow.end, signal),
    enabled: !!orgId,
    staleTime: 60_000,
  });

  // Station name for exported compliance artifacts (shared cache).
  const { data: profile } = useQuery(currentProfileQuery());
  const stationName = profile?.stationName || undefined;

  // Warm-cache roster (shared with the Overview tab) for uploaded-by names.
  const rosterQuery = useQuery({
    ...stationUsersQuery(orgId ?? ""),
    enabled: !!orgId,
  });
  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    // Invitation-only rows have a null id and cannot be activity actors.
    for (const u of rosterQuery.data?.users ?? []) {
      if (u.id) map.set(u.id, formatOfficer(u));
    }
    return map;
  }, [rosterQuery.data]);

  // Prefer the polled detail's run over the history-list snapshot; fall back
  // to the list entry so selecting a run renders its header before the first
  // detail fetch lands.
  const selectedRun = useMemo(() => {
    if (!runsState.selectedRunId) return undefined;
    if (runsState.runDetail?.run.runId === runsState.selectedRunId) {
      return runsState.runDetail.run;
    }
    return runsState.runs.find((r) => r.runId === runsState.selectedRunId);
  }, [runsState.selectedRunId, runsState.runDetail, runsState.runs]);

  return (
    <Container maxWidth="xl" sx={{ overflow: "auto" }}>
      <Box sx={{ p: 3, pb: 4, minHeight: "100vh" }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight={700}>
            Internal Affairs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Upload policy documents and run AI compliance checks against recent
            reports
          </Typography>
        </Box>

        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12, md: 5 }}>
            <PolicyDocsCard
              docs={policyDocs.docs}
              isLoading={policyDocs.isLoading}
              isError={policyDocs.isError}
              onRetry={policyDocs.refetch}
              uploadState={policyDocs.uploadState}
              onUpload={policyDocs.upload}
              onClearUploadError={policyDocs.clearUploadError}
              onDelete={policyDocs.remove}
              removingDocId={policyDocs.removingDocId}
              onMintDownloadUrl={policyDocs.mintDownloadUrl}
              resolveUploaderName={(userId) => nameById.get(userId)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 7 }}>
            <RunComplianceCard
              docs={policyDocs.docs}
              docsLoading={policyDocs.isLoading}
              docsError={policyDocs.isError}
              onRetryDocs={policyDocs.refetch}
              reports={reportsQuery.data?.reports ?? []}
              reportsLoading={reportsQuery.isPending}
              reportsError={reportsQuery.isError}
              onRetryReports={() => void reportsQuery.refetch()}
              onStartRun={runsState.startRun}
              isStarting={runsState.isStarting}
              startError={runsState.startError}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <ComplianceRunsCard
              runs={runsState.runs}
              isLoading={runsState.runsLoading}
              isError={runsState.runsError}
              onRetry={runsState.refetchRuns}
              selectedRunId={runsState.selectedRunId}
              onSelect={runsState.selectRun}
              onLoadAllRuns={listAllComplianceRuns}
              stationName={stationName}
              activeRun={runsState.runDetail?.run}
            />
          </Grid>
          {runsState.selectedRunId && (
            <Grid size={{ xs: 12, md: 7 }} ref={resultsRef}>
              <ComplianceResultsList
                run={selectedRun}
                results={runsState.runDetail?.results}
                isLoading={runsState.runDetailLoading}
                isError={runsState.runDetailError}
                onRetry={runsState.refetchRunDetail}
                stationName={stationName}
              />
            </Grid>
          )}
        </Grid>
      </Box>
    </Container>
  );
};

export default StationAdminInternalAffairs;
